import {
  Download,
  Facebook,
  Linkedin,
  MessageCircle,
  Share2,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./Dialog";

interface ShareEventProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: {
    id: string;
    name: string;
  };
}

export default function ShareEvent({
  open,
  onOpenChange,
  event,
}: ShareEventProps) {
  const getEventURL = (eventId: string) => {
    return `${window.location.origin}/events/${eventId}`;
  };

  const handleDownloadQR = () => {
    const canvas = document.querySelector(
      ".qr-code-canvas canvas"
    ) as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `${event.name.replace(/\s+/g, "-")}-QR.png`;
      link.href = url;
      link.click();
    }
  };

  const handleShareToFacebook = () => {
    const url = getEventURL(event.id);
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  const handleShareToTwitter = () => {
    const url = getEventURL(event.id);
    const text = `Check out ${event.name}!`;
    const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  const handleShareToLinkedIn = () => {
    const url = getEventURL(event.id);
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  const handleShareToWhatsApp = () => {
    const url = getEventURL(event.id);
    const text = `Check out ${event.name}! ${url}`;
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(shareUrl, "_blank");
  };

  const handleNativeShare = async () => {
    const url = getEventURL(event.id);
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.name,
          text: `Check out ${event.name}!`,
          url: url,
        });
      } catch (err) {
        console.log("Error sharing:", err);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="capitalize">{event.name}</DialogTitle>
          <DialogDescription>
            Scan this QR code to view event details
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center mb-6 qr-code-canvas">
          <QRCodeCanvas
            value={getEventURL(event.id)}
            size={256}
            level="H"
            includeMargin={true}
          />
        </div>

        <div className="space-y-4">
          <button
            onClick={handleDownloadQR}
            className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium"
          >
            <Download size={20} />
            Download QR Code
          </button>

          {/* Social Media Share Buttons */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Share to Social Media
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleShareToFacebook}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm"
              >
                <Facebook size={18} />
                Facebook
              </button>
              <button
                onClick={handleShareToTwitter}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 text-sm"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                X
              </button>
              <button
                onClick={handleShareToLinkedIn}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors duration-200 text-sm"
              >
                <Linkedin size={18} />
                LinkedIn
              </button>
              <button
                onClick={handleShareToWhatsApp}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200 text-sm"
              >
                <MessageCircle size={18} />
                WhatsApp
              </button>
            </div>
            {"share" in navigator && (
              <button
                onClick={handleNativeShare}
                className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-sm font-medium"
              >
                <Share2 size={18} />
                More Share Options
              </button>
            )}
          </div>

          <div className="text-center border-t pt-4">
            <p className="text-xs text-gray-500 mb-1">Event URL</p>
            <p className="text-sm text-gray-700 break-all font-mono bg-gray-50 p-2 rounded">
              {getEventURL(event.id)}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
